import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Container,
  Card,
  Typography,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Chip,
  IconButton,
  Alert,
  Snackbar,
  Grid,
  Avatar,
  Tabs,
  Tab,
} from '@mui/material';
import { ArrowBack, Add, Delete, Edit, CloudUpload } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { teamMemberService, extractApiDataList } from '../services/api';
import { getAssetUrl } from '../config';

const ROLE_OPTIONS = [
  { value: 'founder', label: 'Founder' },
  { value: 'co_founder', label: 'Co-Founder' },
  { value: 'team_member', label: 'Team Member' },
];

const emptyForm = {
  name: '',
  role_type: 'team_member',
  designation: '',
  bio: '',
  email: '',
  phone: '',
  linkedin_url: '',
  display_order: '0',
  is_active: '1',
};

const roleChipColor = {
  founder: { bg: '#fef3c7', color: '#92400e' },
  co_founder: { bg: '#dbeafe', color: '#1e40af' },
  team_member: { bg: '#ecfdf3', color: '#166534' },
};

function roleLabel(role) {
  return ROLE_OPTIONS.find((r) => r.value === role)?.label || role;
}

function truncate(str, len = 80) {
  if (!str) return '—';
  const s = String(str).trim();
  return s.length > len ? `${s.slice(0, len)}…` : s;
}

const TeamMembersPage = ({ mode = 'all' }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [filterTab, setFilterTab] = useState('all');
  const [newRow, setNewRow] = useState(emptyForm);
  const [newPhoto, setNewPhoto] = useState(null);
  const [newPreview, setNewPreview] = useState('');
  const isFoundersMode = mode === 'founders';
  const isTeamMode = mode === 'team';
  const addRoleOptions = isFoundersMode
    ? ROLE_OPTIONS.filter((r) => r.value === 'founder' || r.value === 'co_founder')
    : isTeamMode
      ? ROLE_OPTIONS.filter((r) => r.value === 'team_member')
      : ROLE_OPTIONS;

  useEffect(() => {
    setNewRow((prev) => ({
      ...prev,
      role_type: isFoundersMode ? 'founder' : isTeamMode ? 'team_member' : prev.role_type,
    }));
  }, [isFoundersMode, isTeamMode]);

  const [toast, setToast] = useState({ open: false, message: '', type: 'success' });
  const [loadError, setLoadError] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      setLoadError('');
      const res = await teamMemberService.getAll({ include_inactive: 1 });
      setMembers(extractApiDataList(res));
    } catch (e) {
      const msg = e.response?.data?.error || e.message || 'Failed to load team';
      setLoadError(msg);
      setMembers([]);
      setToast({ open: true, message: msg, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    if (isFoundersMode) {
      return members.filter((m) => m.role_type === 'founder' || m.role_type === 'co_founder');
    }
    if (isTeamMode) {
      return members.filter((m) => m.role_type === 'team_member');
    }
    if (filterTab === 'all') return members;
    return members.filter((m) => m.role_type === filterTab);
  }, [members, filterTab, isFoundersMode, isTeamMode]);

  const onPickPhoto = (file) => {
    if (!file) return;
    setNewPhoto(file);
    const reader = new FileReader();
    reader.onload = (ev) => setNewPreview(ev.target?.result || '');
    reader.readAsDataURL(file);
  };

  const buildFormData = (row, photoFile) => {
    const fd = new FormData();
    if (photoFile) fd.append('photo', photoFile);
    fd.append('name', row.name.trim());
    fd.append('role_type', row.role_type);
    fd.append('designation', row.designation || '');
    fd.append('bio', row.bio || '');
    fd.append('email', row.email || '');
    fd.append('phone', row.phone || '');
    fd.append('linkedin_url', row.linkedin_url || '');
    fd.append('display_order', String(row.display_order === '' ? 0 : row.display_order));
    fd.append('is_active', row.is_active === '1' ? '1' : '0');
    return fd;
  };

  const handleAdd = async () => {
    if (!newRow.name.trim()) {
      setToast({ open: true, message: 'Name is required', type: 'error' });
      return;
    }
    try {
      const res = await teamMemberService.create(buildFormData(newRow, newPhoto));
      const created = res.data?.data ?? res.data;
      setNewRow(emptyForm);
      setNewPhoto(null);
      setNewPreview('');
      setToast({ open: true, message: 'Team member added', type: 'success' });
      if (created?.id) {
        navigate(`/admin/team/${created.id}/edit`);
      } else {
        await load();
      }
    } catch (e) {
      setToast({ open: true, message: e.response?.data?.error || 'Add failed', type: 'error' });
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete ${name || 'this team member'}?`)) return;
    try {
      await teamMemberService.delete(id);
      setToast({ open: true, message: 'Deleted', type: 'success' });
      await load();
    } catch (e) {
      setToast({ open: true, message: e.response?.data?.error || 'Delete failed', type: 'error' });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#f5f7fa', minHeight: '100vh', py: 4 }}>
      <Container maxWidth={false} sx={{ maxWidth: '100%' }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/admin/dashboard')} sx={{ mb: 2, color: '#2c5f2d' }}>
          Back
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          {isFoundersMode ? 'Founders Management' : isTeamMode ? 'Team Members Management' : 'Team Management'}
        </Typography>
        <Typography variant="body2" sx={{ color: '#667085', mb: 3 }}>
          {isFoundersMode
            ? 'Manage Founder and Co-Founder profiles. Use Edit for full profile details.'
            : isTeamMode
              ? 'Manage Team Member profiles only. Use Edit for full profile details.'
              : 'Manage Founder, Co-Founder, and team members. Use Edit for full profile details (bio, contact, photo).'}
        </Typography>

        {loadError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {loadError}
          </Alert>
        )}

        <Card sx={{ p: 3, mb: 3, border: '1px solid #e8ecf1' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Quick add (edit full details on next screen)
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={2}>
              <Button variant="outlined" component="label" startIcon={<CloudUpload />} fullWidth>
                Photo
                <input type="file" hidden accept="image/*" onChange={(e) => onPickPhoto(e.target.files?.[0])} />
              </Button>
              {newPreview && <Avatar src={newPreview} sx={{ mt: 1, width: 80, height: 80, mx: 'auto' }} />}
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth size="small" label="Name *" value={newRow.name} onChange={(e) => setNewRow((s) => ({ ...s, name: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField fullWidth size="small" select label="Role *" value={newRow.role_type} onChange={(e) => setNewRow((s) => ({ ...s, role_type: e.target.value }))} SelectProps={{ native: true }}>
                {addRoleOptions.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth size="small" label="Designation" value={newRow.designation} onChange={(e) => setNewRow((s) => ({ ...s, designation: e.target.value }))} />
            </Grid>
            <Grid item xs={6} md={1}>
              <TextField fullWidth size="small" label="Order" type="number" value={newRow.display_order} onChange={(e) => setNewRow((s) => ({ ...s, display_order: e.target.value }))} />
            </Grid>
            <Grid item xs={6} md={1}>
              <TextField fullWidth size="small" select label="Active" value={newRow.is_active} onChange={(e) => setNewRow((s) => ({ ...s, is_active: e.target.value }))} SelectProps={{ native: true }}>
                <option value="1">Yes</option>
                <option value="0">No</option>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Button variant="contained" startIcon={<Add />} onClick={handleAdd} sx={{ backgroundColor: '#2c5f2d' }}>
                Add & edit details
              </Button>
            </Grid>
          </Grid>
        </Card>

        <Card sx={{ border: '1px solid #e8ecf1' }}>
          {!isFoundersMode && !isTeamMode && (
            <Tabs value={filterTab} onChange={(_, v) => setFilterTab(v)} sx={{ px: 2, borderBottom: '1px solid #e8ecf1' }}>
              <Tab label="All" value="all" />
              <Tab label="Founder" value="founder" />
              <Tab label="Co-Founder" value="co_founder" />
              <Tab label="Team" value="team_member" />
            </Tabs>
          )}
          <TableContainer>
            <Table>
              <TableHead sx={{ backgroundColor: '#f8fafb' }}>
                <TableRow>
                  <TableCell>Photo</TableCell>
                  <TableCell>Name & role</TableCell>
                  <TableCell>Designation</TableCell>
                  <TableCell>Bio</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Order</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4, color: '#667085' }}>
                      No members in this group.
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((row) => {
                    const chip = roleChipColor[row.role_type] || roleChipColor.team_member;
                    return (
                      <TableRow key={row.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/admin/team/${row.id}/edit`)}>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Avatar src={getAssetUrl(row.photo_url)} sx={{ width: 56, height: 56 }}>
                            {row.name?.[0]}
                          </Avatar>
                        </TableCell>
                        <TableCell>
                          <Typography fontWeight={600}>{row.name}</Typography>
                          <Chip label={roleLabel(row.role_type)} size="small" sx={{ mt: 0.5, bgcolor: chip.bg, color: chip.color, fontWeight: 600 }} />
                        </TableCell>
                        <TableCell>{row.designation || '—'}</TableCell>
                        <TableCell sx={{ maxWidth: 220 }}>
                          <Typography variant="body2" sx={{ color: '#667085' }}>
                            {truncate(row.bio, 100)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 180 }}>
                          {row.email && (
                            <Typography variant="caption" display="block">
                              {row.email}
                            </Typography>
                          )}
                          {row.phone && (
                            <Typography variant="caption" display="block" sx={{ color: '#667085' }}>
                              {row.phone}
                            </Typography>
                          )}
                          {row.linkedin_url && (
                            <Typography variant="caption" display="block" sx={{ color: '#2c5f2d' }}>
                              LinkedIn
                            </Typography>
                          )}
                          {!row.email && !row.phone && !row.linkedin_url && '—'}
                        </TableCell>
                        <TableCell>{row.display_order}</TableCell>
                        <TableCell>
                          <Chip label={row.is_active ? 'Active' : 'Inactive'} size="small" color={row.is_active ? 'success' : 'default'} />
                        </TableCell>
                        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                          <IconButton size="small" color="primary" onClick={() => navigate(`/admin/team/${row.id}/edit`)} title="Edit full details">
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDelete(row.id, row.name)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Container>

      <Snackbar open={toast.open} autoHideDuration={5000} onClose={() => setToast((t) => ({ ...t, open: false }))}>
        <Alert severity={toast.type} onClose={() => setToast((t) => ({ ...t, open: false }))}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TeamMembersPage;
